/**
 * Sparkle Overlay — places animated SVG twinkles on top of a transparent PNG
 * at points sampled from the image's brightest opaque pixels (i.e. gem
 * highlights). The original image stays visible underneath; sparkles bleed
 * freely past gem boundaries because nothing is mask-clipped.
 *
 * The picking pass: rasterise the source PNG into a small offscreen canvas,
 * keep only opaque pixels, sort them by luminance, then run a Mitchell
 * best-candidate spread over the brightest pool to space N twinkles out.
 *
 * Each twinkle is an SVG element with CSS-driven scale/rotate/fade animation,
 * randomised duration and delay so they twinkle out of phase.
 */

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const SVG_NS = 'http://www.w3.org/2000/svg';
const SPARKLE_PATH = 'M0,-10 Q1.5,-1.5 10,0 Q1.5,1.5 0,10 Q-1.5,1.5 -10,0 Q-1.5,-1.5 0,-10 Z';

class SparkleOverlay extends HTMLElement {
  #placed = false;

  connectedCallback() {
    if (this.#placed) return;

    this.count = clamp(parseInt(this.dataset.count || '40', 10), 1, 500);
    this.minSize = parseFloat(this.dataset.minSize || '10');
    this.maxSize = parseFloat(this.dataset.maxSize || '24');
    this.minDuration = parseFloat(this.dataset.minDuration || '3');
    this.maxDuration = parseFloat(this.dataset.maxDuration || '5');
    this.cycleSpread = parseFloat(this.dataset.cycleSpread || '6');
    this.lumaBiasPool = parseFloat(this.dataset.lumaBiasPool || '0.25');

    const shapeUrl = this.dataset.shapeImage;
    if (!shapeUrl) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => this.#place(img);
    img.onerror = () => {
      console.warn('[sparkle-overlay] failed to load shape image:', shapeUrl);
    };
    img.src = shapeUrl;
  }

  #place(img) {
    if (this.#placed) return;
    this.#placed = true;

    const positions = this.#samplePositions(img);
    if (!positions.length) return;
    this.#renderSparkles(positions);
    this.dataset.loaded = 'true';
  }

  #samplePositions(img) {
    const W = 256;
    const H = Math.max(1, Math.round(W * img.naturalHeight / img.naturalWidth));
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, W, H);

    let pixels;
    try {
      pixels = ctx.getImageData(0, 0, W, H).data;
    } catch (err) {
      console.warn('[sparkle-overlay] CORS read failed (image canvas tainted):', err);
      return [];
    }

    const candidates = [];
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        const i = (y * W + x) * 4;
        if (pixels[i + 3] < 200) continue;
        const luma = 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
        candidates.push({ x: x / W, y: y / H, luma });
      }
    }
    if (!candidates.length) return [];

    // Bias toward gem highlights: keep top fraction by luminance as the candidate pool.
    candidates.sort((a, b) => b.luma - a.luma);
    const poolSize = Math.max(this.count * 6, Math.round(candidates.length * this.lumaBiasPool));
    const pool = candidates.slice(0, Math.min(poolSize, candidates.length));

    // Mitchell best-candidate spatial spread.
    const result = [pool[Math.floor(Math.random() * pool.length)]];
    const target = Math.min(this.count, pool.length);
    while (result.length < target) {
      let bestPoint = null;
      let bestDist = -1;
      for (let attempt = 0; attempt < 30; attempt++) {
        const cand = pool[Math.floor(Math.random() * pool.length)];
        let minD = Infinity;
        for (let i = 0; i < result.length; i++) {
          const r = result[i];
          const dx = r.x - cand.x;
          const dy = r.y - cand.y;
          const d = dx * dx + dy * dy;
          if (d < minD) minD = d;
        }
        if (minD > bestDist) {
          bestDist = minD;
          bestPoint = cand;
        }
      }
      if (!bestPoint) break;
      result.push(bestPoint);
    }
    return result;
  }

  #renderSparkles(positions) {
    const minSize = Math.min(this.minSize, this.maxSize);
    const maxSize = Math.max(this.minSize, this.maxSize);
    const minDur = Math.min(this.minDuration, this.maxDuration);
    const maxDur = Math.max(this.minDuration, this.maxDuration);

    const frag = document.createDocumentFragment();
    for (const p of positions) {
      const size = minSize + Math.random() * (maxSize - minSize);
      const delay = Math.random() * this.cycleSpread;
      const duration = minDur + Math.random() * (maxDur - minDur);

      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('class', 'sparkle-overlay__star');
      svg.setAttribute('viewBox', '-10 -10 20 20');
      svg.setAttribute('aria-hidden', 'true');
      svg.style.cssText = `top:${(p.y * 100).toFixed(2)}%;left:${(p.x * 100).toFixed(2)}%;--sparkle-size:${size.toFixed(1)}px;--sparkle-delay:${delay.toFixed(2)}s;--sparkle-duration:${duration.toFixed(2)}s;`;

      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', SPARKLE_PATH);
      svg.appendChild(path);
      frag.appendChild(svg);
    }
    this.appendChild(frag);
  }
}

if (!customElements.get('sparkle-overlay')) {
  customElements.define('sparkle-overlay', SparkleOverlay);
}
